/// <reference types="@types/google.maps" />

import { AxiosError } from 'axios';
import * as SurveyCore from 'survey-core';
import { FunctionFactory, Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

import * as Sentry from '@sentry/react';
import styles from './_index.module.css';

import '@/assets/css/survey-override.css';
import 'survey-core/defaultV2.min.css';

import { themeJSON } from '@/assets/survey/FordTheme';
import { initSurvey, prepareForSurvey, prepareSurveyOnQuestionAdded } from '@/helpers/surveyTemplatesAll';
import { initSurveyFord } from '@/helpers/surveyTemplatesFord';

import '@/assets/survey/surveysjs_renderers/CheckboxVOITabs';

import { Attendee } from '@/types/attendee';
import { getBootcampById } from '@/services/bootcamp';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import baseSurvey from './PreSurvey.json';

// Register the custom property BEFORE any other survey-related imports
SurveyCore.Serializer.addProperty('autocompleteaddress', {
  name: 'country',
  default: 'us',
  category: 'general',
  visibleIndex: 3,
});

initSurveyFord();
initSurvey();

// Helper function to convert string errors to AxiosError format
const createError = (message: string): AxiosError => {
  return new AxiosError(message, 'ERR_CUSTOM', undefined, undefined, undefined);
};

const extractUTM = () => {
  const search = window.location.search;
  const params = new URLSearchParams(search);

  const utm: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    if (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].includes(key)) {
      utm[key] = value;
    }
  }

  return utm;
};

SurveyCore.setLicenseKey('NDBhNThlYzYtN2EwMy00ZTgxLWIyNGQtOGFkZWJkM2NlNjI3OzE9MjAyNS0wMS0wNA==');

// Register a function to filter models based on make
FunctionFactory.Instance.register(
  'filterModelsByMake',
  function (this: { question: any }, params: any) {
    const makeValue = params[0];
    const modelValue = params[1];

    console.log('filterModelsByMake called with:', { makeValue, modelValue });

    if (!makeValue || !modelValue) return true;

    // Get the model data from the model's properties
    const modelData = this.question.choices.filter((choice: any) => choice.value === modelValue)[0];
    console.log('modelData:', modelData);

    // If we can't find the model data, allow it (this shouldn't happen in normal operation)
    if (!modelData || !modelData.make_id) return true;

    // Return true if the model's make_id matches the selected make
    const result = modelData.make_id === makeValue;
    console.log('Filter result:', result);
    return result;
  },
  true,
);

/**
 * Configure a fresh survey model instance with theme, CSS classes,
 * event handlers, and vehicle model loading logic.
 */
const configureSurveyModel = (model: Model) => {
  model.applyTheme(themeJSON);

  // Add the CSS classes callback
  model.onUpdateQuestionCssClasses.add((_survey, options) => {
    const { question, cssClasses } = options;

    if (question.name === 'signature') {
      cssClasses.root += ' signatureInput';
    }
    if (question.name === 'minorSignature') {
      cssClasses.root += ' signatureInput';
    }
  });

  // Disable cache for loaded choices to ensure we get fresh data every time
  if (model.settings && model.settings.web) {
    model.settings.web.cacheLoadedChoices = false;
  }

  // Set up event handlers immediately after creating the survey
  prepareForSurvey(model);
  prepareSurveyOnQuestionAdded(null, { survey: model });

  // Get the model question
  const modelQuestion = model.getQuestionByName('vehicleDrivenMostModel');
  if (modelQuestion) {
    // Remove the choicesByUrl property
    modelQuestion.choicesByUrl = undefined;
  }

  // Add event listener to trigger model reload when make is changed
  model.onValueChanged.add((sender: any, options: any) => {
    console.log('onValueChanged:', options.name, options.value);

    if (options.name === 'vehicleDrivenMostMake') {
      console.log('Make changed to:', options.value);
      const makeId = options.value;

      // Clear current model selection
      sender.setValue('vehicleDrivenMostModel', null);

      // Find the model question
      const mq = sender.getQuestionByName('vehicleDrivenMostModel');
      if (mq) {
        console.log('Found model question:', mq);

        // Clear current choices
        mq.choices = [];

        // Load models for the selected make
        if (makeId) {
          fetch('https://cdn.latitudewebservices.com/data/models.json')
            .then(response => response.json())
            .then(data => {
              console.log('Loaded models data:', data);

              // Filter the data by make ID
              const filteredData = data.filter((m: any) => m.make_id === makeId);
              console.log('Filtered models:', filteredData);

              // Set the filtered choices
              mq.choices = filteredData.map((item: any) => ({
                value: item.model_id,
                text: item.model,
                make_id: item.make_id,
              }));
            })
            .catch(error => {
              console.error('Error loading models:', error);
            });
        }
      }
    }
  });
};

const PreSurvey = () => {
  const [searchParams] = useSearchParams();
  const attendeeID = searchParams.get('attendeeID');

  const [attendee, setAttendee] = useState<Attendee>();
  const [attendeeLoading, setAttendeeLoading] = useState(true);
  const [surveyModel, setSurveyModel] = useState<Model | null>(null);

  useEffect(() => {
    if (attendeeID) {
      const fetchAttendee = async () => {
        try {
          const response = await fetch(
            `https://${import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX}getAttendee-e2jtv773lq-uc.a.run.app`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ attendeeID }),
            },
          );

          if (!response.ok) {
            console.error('Error fetching attendee:', response);
            setAttendeeLoading(false);
            throw new Error('Failed to fetch attendee');
          }

          const attendeeData = await response.json();

          // Get original product name from bootcamp config instead of attendee record
          // Match by annotatedName (has superscripts) to get the plain name
          let productName = attendeeData.productName; // fallback
          try {
            const bootcamp = await getBootcampById(attendeeData.eventID);
            if (bootcamp) {
              const product = Object.values(bootcamp.products).find(
                p => p.annotatedName === attendeeData.productName || p.name === attendeeData.productName,
              );
              if (product) {
                productName = product.name;
              }
            }
          } catch (error) {
            console.error('Error fetching bootcamp for product name:', error);
          }

          // Store productName on attendeeData so it's available for Towing 101 check
          attendeeData.productName = productName;

          setAttendee(attendeeData as Attendee);
          setAttendeeLoading(false);
        } catch (error) {
          console.error('Error fetching attendee:', error);
          setAttendeeLoading(false);
          throw error;
        }
      };

      fetchAttendee();
    }
  }, [attendeeID]);

  // Create a fresh survey model per attendee to avoid singleton mutation bugs
  useEffect(() => {
    if (!attendee) return;

    // Deep clone JSON to avoid mutating the imported baseSurvey
    const model = new Model(JSON.parse(JSON.stringify(baseSurvey)));

    const isTowing101 = attendee.productName?.toLowerCase().includes('towing 101');

    if (isTowing101) {
      // Hide waiver-related questions
      ['signature', 'waiver_agree', 'hasMinor'].forEach(name => {
        const q = model.getQuestionByName(name);
        if (q) q.visible = false;
      });

      // Hide emergency contact panel
      const emergencyPanel = model.getPanelByName('emergencyContact');
      if (emergencyPanel) emergencyPanel.visible = false;

      // Hide the waiver HTML element
      model.getAllQuestions().forEach(q => {
        if (q.getType() === 'html' && typeof (q as any).html === 'string' && (q as any).html.includes('RELEASE OF CLAIMS')) {
          q.visible = false;
        }
      });

      // Make ALL remaining questions optional for Towing 101
      model.getAllQuestions().forEach(q => {
        q.isRequired = false;
      });
    }

    // Configure theme, CSS classes, event handlers, etc.
    configureSurveyModel(model);

    // Set title and merge attendee data
    model.title = `${attendee.productName} - ${attendee.eventTime}`;

    model.mergeData({
      surveyType: 'preTD',
      attendeeID,
      eventID: attendee.eventID,
      cartID: attendee.cartID,
      firstName: attendee.firstName,
      lastName: attendee.lastName,
      email: attendee.email,
      phone: attendee.phone,
      fordOwner: attendee.fordOwner,
      address_group: (attendee as any).address_group || (attendee as any).addressGroup,
      vin: attendee.fordOwner ? (attendee as any).fordVIN : undefined,
      _utm: extractUTM(),
    });

    // Register completion handler
    model.onComplete.add((sender, options) => {
      sender.setValue('surveyDate', new Date());

      // For Towing 101, waiver was not signed
      sender.setValue('waiverComplete', !isTowing101);

      console.log('onComplete', sender.data);

      const data = {
        ...sender.data,
      };

      // save data to firestore carts collection
      fetch(`https://${import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX}saveSurvey-e2jtv773lq-uc.a.run.app`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          survey: data,
        }),
      })
        .then(() => {
          options.showDataSavingSuccess();
          const redirect = searchParams.get('redirect');
          if (redirect) {
            window.location.href = redirect;
          }
        })
        .catch(err => {
          console.error(err);
          Sentry.captureException(err, {
            tags: {
              page: 'PreDrive',
              location: 'surveyOnComplete',
              type: 'Survey Save Error',
            },
          });
        });
    });

    setSurveyModel(model);
  }, [attendee]);

  return (
    <>
      {attendee ? (
        attendee.waiverComplete ? (
          <>
            <div className="fds-layout-grid__inner">
              <div className="fds-layout-grid__cell--span-12">
                <div className="fmc-billboard towing_billboard fmc-billboard--justify-start fmc-billboard--align-start">
                  <div className="fmc-billboard__image event_billboard_background"></div>
                </div>
              </div>
            </div>
            <div className={styles.lightContent}>
              <div className={styles.contentSection}>
                <h1 className="fds-color__text--tertiary fmc-type--heading4 fmc-mb-6 fmc-mb-5-md">
                  Waiver Already Completed
                </h1>
              </div>
            </div>
          </>
        ) : surveyModel ? (
          <div className={styles.lightContent}>
            <div className={styles.contentSection}>
              <Survey model={surveyModel} />

              <p>
                Disclaimer: Towing capability varies based on cargo, vehicles configuration, accessories, and number and
                weight of passengers. Max tow often requires optional equipment at an extra charge.
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.lightContent}>
            <div className={styles.contentSection}>
              <div>Loading Survey...</div>
            </div>
          </div>
        )
      ) : !attendeeLoading ? (
        <>
          <div className="fds-layout-grid__inner">
            <div className="fds-layout-grid__cell--span-12">
              <div className="fmc-billboard towing_billboard fmc-billboard--justify-start fmc-billboard--align-start">
                <div className="fmc-billboard__image event_billboard_background"></div>
              </div>
            </div>
          </div>
          <div className={styles.lightContent}>
            <div className={styles.contentSection}>
              <h1 className="fds-color__text--tertiary fmc-type--heading4 fmc-mb-6 fmc-mb-5-md">Invalid ID</h1>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.lightContent}>
          <div className={styles.contentSection}>
            <div>Loading Waiver...</div>
          </div>
        </div>
      )}
    </>
  );
};

export default PreSurvey;
